import React, { useMemo } from "react";

import { useLocalContent } from "~/sdks/LocalContent/context";
import { useTranslation } from "react-i18next";

import type { ScoreKey } from "~/sdks/LocalContent/types";

import { ValueIndicator } from "./ValueIndicator";

import * as styles from "./styles";

export function ScoreComparison({
  scoreKey,
  locationScoreValue,
  comparedTo,
}: {
  scoreKey: ScoreKey | null;
  locationScoreValue?: number;
  comparedTo?: { text: string; value: number };
}) {
  const { t } = useTranslation("localContent");
  const { hideScoresList } = useLocalContent();

  const hide = useMemo(
    () => scoreKey === null || hideScoresList?.includes(scoreKey),
    [hideScoresList, scoreKey]
  );

  const comparison = useMemo(() => {
    if (
      typeof locationScoreValue === "undefined" ||
      typeof comparedTo === "undefined"
    ) {
      return null;
    }

    if (locationScoreValue > comparedTo.value) {
      return "higher";
    }

    if (locationScoreValue < comparedTo.value) {
      return "lower";
    }

    return "equal";
  }, [locationScoreValue, comparedTo?.value]);

  if (hide || typeof comparedTo === "undefined" || comparison === null) {
    return null;
  }

  return (
    <div className={styles.container} data-testid="score-comparison">
      <ValueIndicator comparison={comparison} />
      <p>
        {t("scoreComparison.text", {
          context: comparison !== "equal" ? comparison : undefined,
          name: comparedTo.text,
          value: comparedTo.value,
        })}
      </p>
    </div>
  );
}
